// faceTecLoader.js

// Bases do seu CDN
const FACE_TEC_CDN_BASE_URL = 'https://dg4darze9b6sx.cloudfront.net/v9-9.7.133/core-sdk'
const FACE_TEC_CDN_BASE_URL_OPTIONS = 'https://dg4darze9b6sx.cloudfront.net/v9-9.7.133/core-sdk-optional'

// Endpoints
const FACE_TEC_SCRIPT_URL = `${FACE_TEC_CDN_BASE_URL}/FaceTecSDK.js/FaceTecSDK.js`
const FACE_TEC_RESOURCES_URL = `${FACE_TEC_CDN_BASE_URL}/FaceTecSDK.js/resources`
const FACE_TEC_IMAGES_URL = `${FACE_TEC_CDN_BASE_URL}/FaceTec_images`
const FACE_PT_BR_LANGUAGE = `${FACE_TEC_CDN_BASE_URL_OPTIONS}/FaceTecStrings.pt-br.js`

// Cache interno
let sdkLoadPromise = null
const localeLoadCache = new Map()

// Helpers
const ensureBrowser = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    throw new Error('FaceTec SDK requer ambiente de navegador.')
  }
}

const waitForGlobal = (name, { attempts = 50, delay = 20 } = {}) =>
  new Promise((resolve, reject) => {
    let tries = 0
    const tick = () => {
      // eslint-disable-next-line no-undef
      const ref = window[name]
      if (ref) return resolve(ref)
      tries += 1
      if (tries > attempts) return reject(new Error(`Global "${name}" não apareceu a tempo.`))
      setTimeout(tick, delay)
    }
    tick()
  })

const injectScript = (src, { attrs = {}} = {}) =>
  new Promise((resolve, reject) => {
    ensureBrowser()

    // Reutiliza se já existir um <script src="...">
    const existing = Array.from(document.getElementsByTagName('script')).find((s) => s.src === src)
    if (existing) {
      if (existing.dataset._loaded === '1') return resolve(existing)
      existing.addEventListener('load', () => resolve(existing))
      existing.addEventListener('error', () => reject(new Error(`Falha ao carregar script: ${src}`)))
      return
    }

    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.async = true
    script.src = src
    script.setAttribute('data-facetec', '1')
    Object.entries(attrs).forEach(([k, v]) => script.setAttribute(k, v))

    script.onload = () => {
      script.dataset._loaded = '1'
      resolve(script)
    }
    script.onerror = () => reject(new Error(`Falha ao carregar script: ${src}`))

    document.head.appendChild(script)
  })

const loadCommonJsScript = (src) =>
  new Promise((resolve, reject) => {
    ensureBrowser()

    const previousModule = window.module
    const previousExports = window.exports
    const moduleShim = { exports: {}}

    window.module = moduleShim
    window.exports = moduleShim.exports

    injectScript(src, { attrs: { 'data-facetec-locale': 'true' }})
      .then(() => {
        window.module = previousModule
        window.exports = previousExports
        resolve(moduleShim.exports && Object.keys(moduleShim.exports).length ? moduleShim.exports : null)
      })
      .catch((error) => {
        window.module = previousModule
        window.exports = previousExports
        reject(error)
      })
  })

const resolveWithSdk = (resolve, reject) => {
  try {
    ensureBrowser()
    // eslint-disable-next-line no-undef
    if (typeof FaceTecSDK !== 'undefined') {
      // eslint-disable-next-line no-undef
      return resolve(FaceTecSDK)
    }
    return reject(new Error('FaceTec SDK não inicializou corretamente.'))
  } catch (e) {
    return reject(e)
  }
}

// API pública
export const FaceTecCdnPaths = {
  baseUrl: FACE_TEC_CDN_BASE_URL,
  scriptUrl: FACE_TEC_SCRIPT_URL,
  resourcesUrl: FACE_TEC_RESOURCES_URL,
  imagesUrl: FACE_TEC_IMAGES_URL,
  languagePTBR: FACE_PT_BR_LANGUAGE
}

export const ensureFaceTecSdkLoaded = () => {
  ensureBrowser()

  // eslint-disable-next-line no-undef
  if (typeof FaceTecSDK !== 'undefined') {
    // eslint-disable-next-line no-undef
    return Promise.resolve(FaceTecSDK)
  }

  if (sdkLoadPromise) return sdkLoadPromise

  sdkLoadPromise = new Promise(async(resolve, reject) => {
    try {
      await injectScript(FACE_TEC_SCRIPT_URL, { attrs: { 'data-facetec-sdk': 'true' }})
      resolveWithSdk(resolve, reject)
    } catch (e) {
      sdkLoadPromise = null
      reject(e)
    }
  })

  return sdkLoadPromise
}

/**
 * Carrega o arquivo de localização (JS do FaceTec) OU um JSON de tradução.
 * - Se a URL terminar com ".js": injeta o script e tenta descobrir o objeto global exportado.
 * - Se terminar com ".json": faz fetch e retorna o objeto JSON.
 *
 * @param {string} url - URL do arquivo de idioma (ex.: FaceTecStrings.pt-br.js)
 * @param {object} options - { globalHints?: string[] } possíveis nomes globais expostos pelo .js
 * @returns {Promise<object>} objeto de tradução a ser passado ao configureLocalization
 */
export const loadFaceTecLocalization = async(url, { globalHints = ['FaceTecStrings', 'FaceTecLocale', 'FaceTecLocalization'] } = {}) => {
  ensureBrowser()

  if (localeLoadCache.has(url)) return localeLoadCache.get(url)

  const p = (async() => {
    if (url.endsWith('.json')) {
      const res = await fetch(url, { credentials: 'omit' })
      if (!res.ok) throw new Error(`Falha ao baixar JSON de localização: ${url}`)
      return res.json()
    }

    // Assume .js (arquivo oficial "FaceTecStrings.xx.js")
    const commonJsExport = await loadCommonJsScript(url)
    if (commonJsExport) {
      if (typeof commonJsExport === 'function') return commonJsExport()
      if (typeof commonJsExport === 'object') return commonJsExport
    }

    // Descobre o objeto global exposto
    for (const hint of globalHints) {
      try {
        const obj = await waitForGlobal(hint, { attempts: 50, delay: 20 })
        if (obj && (typeof obj === 'object' || typeof obj === 'function')) {
          // Algumas variantes exportam uma função que retorna o dicionário:
          // ex.: window.FaceTecStrings() -> { ... }
          return typeof obj === 'function' ? obj() : obj
        }
      } catch {
        // tenta o próximo hint
      }
    }

    // Como fallback: alguns bundles colocam algo como window.FaceTecStringsPtBr
    const anyGlobal = Object.keys(window).find((k) => /^FaceTec.*(Strings|Locale|Localization)/i.test(k))
    // eslint-disable-next-line no-undef
    const maybe = anyGlobal ? window[anyGlobal] : null
    if (maybe) return typeof maybe === 'function' ? maybe() : maybe

    throw new Error('Não foi possível encontrar o objeto de localização após carregar o script.')
  })()

  localeLoadCache.set(url, p)
  return p
}

export const getFaceTecSDK = () => {
  ensureBrowser()
  // eslint-disable-next-line no-undef
  if (typeof FaceTecSDK === 'undefined') {
    throw new Error('FaceTec SDK ainda não foi carregado.')
  }
  // eslint-disable-next-line no-undef
  return FaceTecSDK
}
